import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { productsApi } from '../../api/products.js';

const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

export default function ProductCategoriesPage() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editTarget, setEditTarget] = useState(null); // null | { isNew: true } | category object
  const [form, setForm] = useState({ name: '', hsn_code: '' });
  const [saving, setSaving] = useState(false);
  const nameRef = useRef(null);

  const load = () => {
    setLoading(true);
    productsApi.getCategories()
      .then(res => setCategories(res.data || []))
      .catch(() => toast.error('Failed to load categories.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setForm({ name: '', hsn_code: '' });
    setEditTarget({ isNew: true });
    setTimeout(() => nameRef.current?.focus(), 50);
  };

  const openEdit = (c) => {
    setForm({ name: c.name, hsn_code: c.hsn_code || '' });
    setEditTarget(c);
    setTimeout(() => nameRef.current?.focus(), 50);
  };

  const cancel = () => setEditTarget(null);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const payload = { name: form.name.trim(), hsn_code: form.hsn_code.trim() || null };
      if (editTarget.isNew) {
        await productsApi.createCategory(payload);
        toast.success('Category created!');
      } else {
        await productsApi.updateCategory(editTarget.id, payload);
        toast.success('Category updated!');
      }
      setEditTarget(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save category.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-body">
      <div className="card">
        <div className="card-header">
          <div>
            <span className="card-title">Product Categories</span>
            <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 13 }}>
              {loading ? 'Loading…' : `${categories.length} categor${categories.length !== 1 ? 'ies' : 'y'}`}
            </p>
          </div>
          <button className="btn btn-primary btn-sm" onClick={openNew}>+ Add Category</button>
        </div>

        {editTarget && (
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12, color: '#374151' }}>
              {editTarget.isNew ? 'New Category' : `Edit: ${editTarget.name}`}
            </div>
            <form onSubmit={handleSave}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 200px' }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>Category Name *</label>
                  <input
                    ref={nameRef}
                    className="form-control"
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. THERMAL PAPER"
                  />
                </div>
                <div style={{ flex: '1 1 160px' }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>HSN Code</label>
                  <input
                    className="form-control"
                    style={{ fontFamily: 'monospace' }}
                    value={form.hsn_code}
                    onChange={e => setForm(f => ({ ...f, hsn_code: e.target.value }))}
                    placeholder="e.g. 48119099"
                  />
                </div>
                <div style={{ display: 'flex', gap: 8, paddingBottom: 1 }}>
                  <button type="submit" className="btn btn-primary btn-sm" disabled={saving || !form.name.trim()}>
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                  <button type="button" className="btn btn-outline btn-sm" onClick={cancel}>Cancel</button>
                </div>
              </div>
            </form>
          </div>
        )}

        <div className="card-body" style={{ padding: 0 }}>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 44 }}>#</th>
                  <th>Category Name</th>
                  <th>HSN Code</th>
                  <th>Products</th>
                  <th style={{ width: 80 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 4 }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({ length: 5 }).map((__, j) => (
                        <td key={j}><div style={{ height: 14, borderRadius: 4, background: 'linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 50%,#f1f5f9 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.2s infinite', width: j === 1 ? '60%' : '40%' }} /></td>
                      ))}
                    </tr>
                  ))
                ) : categories.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', padding: '48px 0', color: '#94a3b8' }}>
                      <div style={{ fontSize: 36, marginBottom: 8 }}>🏷️</div>
                      <p style={{ fontWeight: 600, margin: '0 0 12px' }}>No categories yet</p>
                      <button className="btn btn-primary btn-sm" onClick={openNew}>+ Add First Category</button>
                    </td>
                  </tr>
                ) : (
                  categories.map((c, i) => (
                    <tr key={c.id}>
                      <td style={{ color: '#94a3b8', fontSize: 12 }}>{i + 1}</td>
                      <td style={{ fontWeight: 700, fontSize: 14 }}>{c.name}</td>
                      <td>
                        {c.hsn_code
                          ? <span style={{ fontFamily: 'monospace', fontSize: 13, background: '#f1f5f9', padding: '2px 8px', borderRadius: 4 }}>{c.hsn_code}</span>
                          : <span style={{ color: '#cbd5e1' }}>—</span>}
                      </td>
                      <td style={{ color: '#64748b', fontSize: 13 }}>
                        {c.product_count != null ? `${c.product_count} products` : '—'}
                      </td>
                      <td>
                        <button className="btn btn-xs btn-outline" title="Edit" onClick={() => openEdit(c)}>✎ Edit</button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
